// Modern fetch-based API client with automatic error handling and retries
import { z } from 'zod';
import { config } from '../../config';

export interface ApiConfig {
  baseURL: string;
  timeout: number;
  retries: number;
  retryDelay: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
  error_code?: string;
  request_id?: string;
  timestamp?: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isRetryable(): boolean {
    return [408, 409, 429, 500, 502, 503, 504].includes(this.status);
  }
}

export class BaseApi {
  private config: ApiConfig;
  private csrfToken: string | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;
  private failedRequestsQueue: Array<() => void> = [];
  private onCsrfTokenUpdate?: (token: string) => void;

  constructor(configOverrides: Partial<ApiConfig> = {}) {
    this.config = {
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      retries: 3,
      retryDelay: 1000,
      ...configOverrides,
    };
  }

  setCsrfToken(token: string): void {
    this.csrfToken = token;
  }

  /**
   * Set a callback to be called when CSRF token is updated (e.g., after refresh)
   */
  setOnCsrfTokenUpdate(callback: (token: string) => void): void {
    this.onCsrfTokenUpdate = callback;
  }

  /**
   * Determine if a 401 error should trigger a token refresh attempt
   */
  private shouldAttemptRefresh(endpoint: string, skipAuth?: boolean): boolean {
    // Don't refresh if auth is explicitly skipped
    if (skipAuth) return false;

    // Don't refresh on auth endpoints (except session check)
    if (endpoint.includes('/api/auth/login')) return false;
    if (endpoint.includes('/api/auth/refresh')) return false;
    if (endpoint.includes('/api/auth/logout')) return false;

    // Refresh on all other 401s
    return true;
  }

  /**
   * Attempt to refresh the access token using the refresh token cookie
   */
  private async refreshAccessToken(): Promise<void> {
    // If already refreshing, wait for that to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start the refresh process
    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${this.config.baseURL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include', // Include cookies
          headers: {
            'Accept': 'application/json',
            'X-Client': 'stash-web',
          },
        });

        if (!response.ok) {
          throw new Error('Token refresh failed');
        }

        const data = await response.json();

        // Update CSRF token if provided
        if (data.csrf_token) {
          this.setCsrfToken(data.csrf_token);

          // Notify listeners (e.g., auth store) about the new CSRF token
          if (this.onCsrfTokenUpdate) {
            this.onCsrfTokenUpdate(data.csrf_token);
          }
        }

        // Resolve all queued requests
        this.failedRequestsQueue.forEach(callback => callback());
        this.failedRequestsQueue = [];
      } catch (error) {
        // Clear queue on failure
        this.failedRequestsQueue = [];

        // Redirect to login page (but not if already on login page to prevent infinite loop)
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }

        throw error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async fetchWithTimeout(
    url: string, 
    options: RequestInit, 
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async request<T = any>(
    endpoint: string,
    options: RequestInit & { 
      skipRetry?: boolean;
      skipAuth?: boolean;
      timeout?: number;
    } = {}
  ): Promise<T> {
    const {
      skipRetry = false,
      skipAuth = false,
      timeout = this.config.timeout,
      ...fetchOptions
    } = options;

    const url = `${this.config.baseURL}${endpoint}`;
    
    const headers = new Headers(fetchOptions.headers);
    
    // Add default headers
    if (!headers.has('Content-Type') && fetchOptions.body && typeof fetchOptions.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }
    
    // Add auth headers
    if (!skipAuth && this.csrfToken) {
      headers.set('X-CSRF-Token', this.csrfToken);
    }
    
    headers.set('Accept', 'application/json');
    headers.set('X-Client', 'stash-web');

    const requestOptions: RequestInit = {
      ...fetchOptions,
      headers,
      credentials: 'include',
    };

    let lastError: Error | null = null;
    const maxAttempts = skipRetry ? 1 : this.config.retries + 1;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await this.fetchWithTimeout(url, requestOptions, timeout);
        
        // Handle non-JSON responses
        const contentType = response.headers.get('content-type');
        if (!contentType?.includes('application/json')) {
          if (!response.ok) {
            throw new ApiError(
              response.status,
              'HTTP_ERROR',
              `HTTP ${response.status}: ${response.statusText}`
            );
          }
          return null as T;
        }

        const data: ApiResponse<T> = await response.json();

        // Handle successful responses
        if (response.ok && data.success !== false) {
          return data.data ?? data as T;
        }

        // Handle API errors
        const error = new ApiError(
          response.status,
          data.error_code || 'API_ERROR',
          data.error || data.message || `Request failed with status ${response.status}`,
          data
        );

        // Handle 401 errors with automatic token refresh
        if (error.status === 401 && this.shouldAttemptRefresh(endpoint, skipAuth)) {
          try {
            // Attempt to refresh the access token
            await this.refreshAccessToken();

            // Retry the original request after successful refresh
            return this.request<T>(endpoint, options);
          } catch (refreshError) {
            // Refresh failed, throw the original 401 error
            throw error;
          }
        }

        // Don't retry non-retryable errors
        if (!error.isRetryable || skipRetry) {
          throw error;
        }

        lastError = error;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Don't retry network errors that aren't retryable
        if (lastError instanceof ApiError && !lastError.isRetryable) {
          throw lastError;
        }

        // Don't retry on the last attempt
        if (attempt === maxAttempts - 1) {
          throw lastError;
        }
      }

      // Wait before retrying
      if (attempt < maxAttempts - 1) {
        const delay = this.config.retryDelay * Math.pow(2, attempt);
        await this.sleep(delay);
      }
    }

    throw lastError || new Error('Request failed after all retries');
  }

  // Convenience methods
  async get<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // File upload with progress
  async upload<T = any>(
    endpoint: string,
    data: FormData | ArrayBuffer | Blob,
    options: RequestInit & {
      onProgress?: (loaded: number, total: number) => void;
    } = {}
  ): Promise<T> {
    const { onProgress, ...requestOptions } = options;
    
    // For progress tracking, we'll need to use XMLHttpRequest
    if (onProgress && data instanceof FormData) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const url = `${this.config.baseURL}${endpoint}`;
        
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            onProgress(event.loaded, event.total);
          }
        };
        
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response.data ?? response);
            } catch {
              resolve(xhr.responseText as T);
            }
          } else {
            reject(new ApiError(xhr.status, 'UPLOAD_ERROR', xhr.statusText));
          }
        };
        
        xhr.onerror = () => {
          reject(new ApiError(0, 'NETWORK_ERROR', 'Network error during upload'));
        };
        
        xhr.open(requestOptions.method || 'POST', url);
        
        // Add headers except Content-Type (let browser set it for FormData)
        if (this.csrfToken) {
          xhr.setRequestHeader('X-CSRF-Token', this.csrfToken);
        }
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('X-Client', 'stash-web');
        xhr.withCredentials = true;
        
        xhr.send(data);
      });
    }

    return this.request<T>(endpoint, {
      ...requestOptions,
      method: requestOptions.method || 'POST',
      body: data,
      headers: {
        ...requestOptions.headers,
        // Don't set Content-Type for binary data
      },
    });
  }
}

// Validation schemas
export const createApiResponseSchema = <T>(dataSchema: z.ZodType<T>) =>
  z.object({
    success: z.boolean(),
    data: dataSchema,
    message: z.string().optional(),
    error: z.string().optional(),
    error_code: z.string().optional(),
    request_id: z.string().optional(),
    timestamp: z.string().optional(),
  });

// Create singleton instance
export const api = new BaseApi();